// client/src/pages/Admin/ProjectManager/hooks/usePagination.ts
/**
 * USE PAGINATION HOOK
 * 
 * This hook manages pagination state and calculations.
 * 
 * @module ProjectManager/Hooks/usePagination
 */

import { useState, useMemo, useCallback } from 'react';
import { DEFAULT_PAGE_SIZE, MAX_VISIBLE_PAGES } from '../constants/project.constants';

interface UsePaginationReturn {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    paginatedItems: any[];
    startIndex: number;
    endIndex: number;
    pageNumbers: (number | string)[];
    goToPage: (page: number) => void;
    goToFirstPage: () => void;
    goToLastPage: () => void;
    goToPreviousPage: () => void;
    goToNextPage: () => void;
    changePageSize: (size: number) => void;
    resetPagination: () => void;
}

/**
 * usePagination hook
 * 
 * @param items - Array of items to paginate
 * @param initialPageSize - Initial page size (default: 10)
 * @returns Pagination state and actions
 * 
 * @example
 * const { paginatedItems, currentPage, goToPage, changePageSize } = usePagination(projects, 10);
 */
export const usePagination = <T>(
    items: T[],
    initialPageSize: number = DEFAULT_PAGE_SIZE
): UsePaginationReturn => {
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(initialPageSize);

    // Calculate total pages
    const totalPages = useMemo(() => {
        if (!Array.isArray(items)) return 0;
        return Math.ceil(items.length / pageSize) || 1;
    }, [items, pageSize]);

    // Calculate start and end indices
    const startIndex = useMemo(() => {
        return (currentPage - 1) * pageSize;
    }, [currentPage, pageSize]);

    const endIndex = useMemo(() => {
        return Math.min(startIndex + pageSize, items.length);
    }, [startIndex, pageSize, items]);

    // Get paginated items
    const paginatedItems = useMemo(() => {
        if (!Array.isArray(items)) return [];
        return items.slice(startIndex, endIndex);
    }, [items, startIndex, endIndex]);

    // Generate page numbers
    const pageNumbers = useMemo(() => {
        if (totalPages <= 1) return [1];

        const numbers: (number | string)[] = [];
        const half = Math.floor(MAX_VISIBLE_PAGES / 2);
        let start = Math.max(1, currentPage - half);
        let end = Math.min(totalPages, start + MAX_VISIBLE_PAGES - 1);

        if (end - start < MAX_VISIBLE_PAGES - 1) {
            start = Math.max(1, end - MAX_VISIBLE_PAGES + 1);
        }

        if (start > 1) {
            numbers.push(1);
            if (start > 2) numbers.push('...');
        }

        for (let i = start; i <= end; i++) {
            numbers.push(i);
        }

        if (end < totalPages) {
            if (end < totalPages - 1) numbers.push('...');
            numbers.push(totalPages);
        }

        return numbers;
    }, [totalPages, currentPage]);

    /**
     * Navigate to a specific page
     */
    const goToPage = useCallback((page: number) => {
        const target = Math.max(1, Math.min(page, totalPages));
        setCurrentPage(target);
    }, [totalPages]);

    /**
     * Navigate to the first page
     */
    const goToFirstPage = useCallback(() => {
        setCurrentPage(1);
    }, []);

    /**
     * Navigate to the last page
     */
    const goToLastPage = useCallback(() => {
        setCurrentPage(totalPages);
    }, [totalPages]);

    /**
     * Navigate to the previous page
     */
    const goToPreviousPage = useCallback(() => {
        setCurrentPage(prev => Math.max(1, prev - 1));
    }, []);

    /**
     * Navigate to the next page
     */
    const goToNextPage = useCallback(() => {
        setCurrentPage(prev => Math.min(totalPages, prev + 1));
    }, [totalPages]);

    /**
     * Change page size
     */
    const changePageSize = useCallback((newSize: number) => {
        setPageSize(newSize);
        setCurrentPage(1);
    }, []);

    /**
     * Reset pagination to default
     */
    const resetPagination = useCallback(() => {
        setCurrentPage(1);
        setPageSize(initialPageSize);
    }, [initialPageSize]);

    return {
        currentPage,
        pageSize,
        totalPages,
        paginatedItems,
        startIndex: startIndex + 1,
        endIndex,
        pageNumbers,
        goToPage,
        goToFirstPage,
        goToLastPage,
        goToPreviousPage,
        goToNextPage,
        changePageSize,
        resetPagination
    };
};